/**
 * Formats URLs to ensure they have the correct protocol
 * Handles NIIO-specific URL patterns:
 * - Full URLs (https://example.com) - returns as is
 * - Protocol-relative URLs (//media-dev.niio.dev/...) - adds https:
 * - S3 URLs (//s3.amazonaws.com/...) - adds https:
 * - Data URLs (data:image/...) - returns as is
 * - Empty strings or null - returns placeholder
 */
import { PLACEHOLDER_IMAGE } from './placeholder';

export function formatImageUrl(url: string | undefined | null): string {
    if (!url || url === '') {
        return PLACEHOLDER_IMAGE;
    }

    // Data URLs - return as is
    if (url.startsWith('data:')) {
        return url;
    }

    // Already has protocol
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }

    // Protocol-relative URL (most common case in NIIO)
    if (url.startsWith('//')) {
        return 'https:' + url;
    }

    // For any other case, return placeholder to avoid 404s
    return PLACEHOLDER_IMAGE;
}

/**
 * Formats video URLs similar to image URLs
 */
export function formatVideoUrl(url: string | undefined | null): string | null {
    if (!url) return null;

    // Already has protocol
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }

    // Protocol-relative URL
    if (url.startsWith('//')) {
        return 'https:' + url;
    }

    // Relative path starting with /
    if (url.startsWith('/')) {
        // In development, use the API base URL domain
        // In production, this would be handled by the CDN
        const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || '';
        if (apiBaseUrl.includes('local') || apiBaseUrl.includes('dev')) {
            // For local/dev, use the same domain as the API
            const domain = new URL(apiBaseUrl).origin;
            return domain + url;
        }
        // For production, use cdn.niio.com
        return 'https://cdn.niio.com' + url;
    }

    // Path without leading slash
    const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || '';
    if (apiBaseUrl.includes('local') || apiBaseUrl.includes('dev')) {
        const domain = new URL(apiBaseUrl).origin;
        return domain + '/' + url;
    }
    return 'https://cdn.niio.com/' + url;
}
